import type { AudioMix } from '@/arcade'

export type GameSfx =
  | 'click'
  | 'confirm'
  | 'back'
  | 'hover'
  | 'coin'
  | 'gem'
  | 'powerup'
  | 'hit'
  | 'explode'
  | 'shoot'
  | 'levelup'
  | 'lose'
  | 'win'
  | 'place'
  | 'clear'
  | 'error'
  | 'key'
  | 'unlock'
  | 'teleport'
  | 'boss'

export type MusicTheme = 'ambient' | 'arcade' | 'tense' | 'zen' | 'triumph' | 'forest' | 'cave' | 'ice'

interface SfxSpec {
  takes: string[]
  gain: number
  rate: [number, number]
}

const BASE = `${import.meta.env.BASE_URL}audio/`

const SFX: Record<GameSfx, SfxSpec> = {
  click: { takes: ['ui_click.mp3'], gain: 0.5, rate: [0.98, 1.06] },
  confirm: { takes: ['ui_confirm.mp3'], gain: 0.7, rate: [0.98, 1.04] },
  back: { takes: ['ui_back.mp3'], gain: 0.6, rate: [0.97, 1.05] },
  hover: { takes: ['ui_click.mp3'], gain: 0.22, rate: [1.1, 1.2] },
  place: { takes: ['slide_a.mp3', 'slide_b.mp3'], gain: 0.6, rate: [0.9, 1.12] },
  coin: { takes: ['coin_a.mp3', 'coin_b.mp3', 'coin_c.mp3'], gain: 0.7, rate: [0.92, 1.12] },
  gem: { takes: ['gem_a.mp3', 'gem_b.mp3'], gain: 0.8, rate: [0.94, 1.08] },
  powerup: { takes: ['powerup_a.mp3', 'powerup_b.mp3'], gain: 0.85, rate: [0.95, 1.06] },
  hit: { takes: ['bonk_a.mp3', 'bonk_b.mp3'], gain: 0.8, rate: [0.88, 1.14] },
  explode: { takes: ['explode_a.mp3', 'explode_b.mp3'], gain: 0.9, rate: [0.9, 1.1] },
  shoot: { takes: ['slide_a.mp3'], gain: 0.4, rate: [1.1, 1.3] },
  levelup: { takes: ['powerup_a.mp3', 'powerup_b.mp3'], gain: 0.9, rate: [0.96, 1.02] },
  lose: { takes: ['lose_a.mp3', 'lose_b.mp3'], gain: 0.9, rate: [0.97, 1.03] },
  win: { takes: ['win_a.mp3', 'win_b.mp3'], gain: 1.0, rate: [0.98, 1.02] },
  clear: { takes: ['teleport_a.mp3'], gain: 0.7, rate: [0.95, 1.08] },
  error: { takes: ['ui_back.mp3'], gain: 0.5, rate: [0.82, 0.9] },
  key: { takes: ['key_a.mp3'], gain: 0.75, rate: [0.95, 1.08] },
  unlock: { takes: ['unlock_a.mp3'], gain: 0.8, rate: [0.96, 1.05] },
  teleport: { takes: ['teleport_a.mp3'], gain: 0.8, rate: [0.9, 1.1] },
  boss: { takes: ['boss_stinger.mp3'], gain: 1.0, rate: [1, 1] },
}

const MUSIC: Record<MusicTheme, string> = {
  ambient: 'music_cave.mp3',
  arcade: 'music_forest.mp3',
  tense: 'music_tense.mp3',
  zen: 'music_forest.mp3',
  triumph: 'music_forest.mp3',
  forest: 'music_forest.mp3',
  cave: 'music_cave.mp3',
  ice: 'amb_underwater.mp3',
}

export const AUDIO_ASSET_FILES = Object.freeze(Array.from(new Set([...Object.values(SFX).flatMap((spec) => spec.takes), ...Object.values(MUSIC)])))

const STORE_KEY = 'arcade:audio-mix'

function loadMix(): AudioMix {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    if (raw) return { master: 0.9, music: 0.55, sfx: 0.9, muted: false, ...JSON.parse(raw) }
  } catch {
    /* ignore */
  }
  return { master: 0.9, music: 0.55, sfx: 0.9, muted: false }
}

/**
 * Real-sample audio manager: plays pre-generated ElevenLabs mp3s through WebAudio
 * with per-play pitch/rate variation. Underwater biomes route music through a
 * biquad lowpass for a muffled submerged feel. A boss-kill stinger drops the music
 * volume and layers a slow-motion sting. Mirrors the vendored AudioEngine surface
 * (play/startMusic/stopMusic/unlock/set*) so game code is a drop-in swap.
 */
export class GameAudioManager {
  private ctx: AudioContext | null = null
  private master: GainNode | null = null
  private musicGain: GainNode | null = null
  private sfxGain: GainNode | null = null
  private musicFilter: BiquadFilterNode | null = null
  private mix: AudioMix = loadMix()
  private ready = false
  private starting: Promise<void> | null = null

  private buffers = new Map<string, AudioBuffer>()
  private loading = new Map<string, Promise<AudioBuffer | null>>()

  private musicSource: AudioBufferSourceNode | null = null
  private currentTheme: string | null = null
  private underwater = false

  get mixState(): AudioMix {
    return { ...this.mix }
  }

  async unlock(): Promise<void> {
    if (this.ready) return
    if (this.starting) return this.starting
    this.starting = (async () => {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      this.ctx = new Ctx()
      if (this.ctx.state === 'suspended') await this.ctx.resume()
      this.master = this.ctx.createGain()
      this.master.gain.value = this.mix.muted ? 0 : this.mix.master
      this.master.connect(this.ctx.destination)
      this.sfxGain = this.ctx.createGain()
      this.sfxGain.gain.value = this.mix.sfx
      this.sfxGain.connect(this.master)
      this.musicFilter = this.ctx.createBiquadFilter()
      this.musicFilter.type = 'lowpass'
      this.musicFilter.frequency.value = 20000
      this.musicGain = this.ctx.createGain()
      this.musicGain.gain.value = this.mix.music
      this.musicFilter.connect(this.musicGain)
      this.musicGain.connect(this.master)
      this.ready = true
      // Warm the common SFX so first plays are instant.
      void this.load('ui_click.mp3')
      void this.load('slide_a.mp3')
      void this.load('coin_a.mp3')
    })()
    return this.starting
  }

  private async load(file: string): Promise<AudioBuffer | null> {
    if (!this.ctx) return null
    const cached = this.buffers.get(file)
    if (cached) return cached
    const pending = this.loading.get(file)
    if (pending) return pending
    const p = (async () => {
      try {
        const res = await fetch(BASE + file)
        if (!res.ok) return null
        const arr = await res.arrayBuffer()
        const buf = await this.ctx!.decodeAudioData(arr)
        this.buffers.set(file, buf)
        return buf
      } catch {
        return null
      } finally {
        this.loading.delete(file)
      }
    })()
    this.loading.set(file, p)
    return p
  }

  private persist() {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(this.mix))
    } catch {
      /* ignore */
    }
  }

  setMaster(v: number) {
    this.mix.master = clamp01(v)
    if (this.master) this.master.gain.value = this.mix.muted ? 0 : this.mix.master
    this.persist()
  }
  setMusic(v: number) {
    this.mix.music = clamp01(v)
    if (this.musicGain) this.musicGain.gain.value = this.mix.music
    this.persist()
  }
  setSfx(v: number) {
    this.mix.sfx = clamp01(v)
    if (this.sfxGain) this.sfxGain.gain.value = this.mix.sfx
    this.persist()
  }
  setMuted(m: boolean) {
    this.mix.muted = m
    if (this.master) this.master.gain.value = m ? 0 : this.mix.master
    this.persist()
  }
  toggleMute() {
    this.setMuted(!this.mix.muted)
    return this.mix.muted
  }

  private rand(a: number, b: number) {
    return a + Math.random() * (b - a)
  }

  play(name: GameSfx) {
    if (!this.ready || this.mix.muted || !this.ctx || !this.sfxGain) return
    const spec = SFX[name]
    if (!spec) return
    const file = spec.takes[(Math.random() * spec.takes.length) | 0]
    void this.load(file).then((buf) => {
      if (!buf || !this.ctx || !this.sfxGain || this.mix.muted) return
      const src = this.ctx.createBufferSource()
      src.buffer = buf
      src.playbackRate.value = this.rand(spec.rate[0], spec.rate[1])
      const g = this.ctx.createGain()
      g.gain.value = spec.gain
      src.connect(g)
      g.connect(this.sfxGain)
      src.start()
    })
  }

  /** Slow-motion boss-kill stinger: duck the music briefly, layer the sting. */
  bossStinger() {
    if (!this.ready || this.mix.muted || !this.ctx || !this.musicGain) return
    const now = this.ctx.currentTime
    const g = this.musicGain.gain
    g.cancelScheduledValues(now)
    g.setValueAtTime(g.value, now)
    g.linearRampToValueAtTime(this.mix.music * 0.2, now + 0.08)
    g.linearRampToValueAtTime(this.mix.music, now + 2.4)
    this.play('boss')
  }

  setUnderwater(on: boolean) {
    this.underwater = on
    if (!this.musicFilter || !this.ctx) return
    const now = this.ctx.currentTime
    this.musicFilter.frequency.cancelScheduledValues(now)
    this.musicFilter.frequency.setValueAtTime(this.musicFilter.frequency.value, now)
    this.musicFilter.frequency.linearRampToValueAtTime(on ? 480 : 20000, now + 0.5)
    if (this.musicFilter.Q) this.musicFilter.Q.value = on ? 0.7 : 0.0001
  }

  startMusic(theme: MusicTheme = 'ambient', seed = 1) {
    if (!this.ready || !this.ctx || !this.musicFilter) return
    const key = `${theme}:${seed}`
    if (this.currentTheme === key && this.musicSource) return
    const file = MUSIC[theme] ?? MUSIC.ambient
    this.stopMusic()
    this.currentTheme = key
    void this.load(file).then((buf) => {
      if (!buf || !this.ctx || !this.musicFilter || this.currentTheme !== key) return
      const src = this.ctx.createBufferSource()
      src.buffer = buf
      src.loop = true
      src.connect(this.musicFilter)
      src.start()
      this.musicSource = src
      if (this.musicGain) {
        const now = this.ctx.currentTime
        this.musicGain.gain.setValueAtTime(0, now)
        this.musicGain.gain.linearRampToValueAtTime(this.mix.music, now + 1.2)
      }
      if (this.underwater) this.setUnderwater(true)
    })
  }

  stopMusic() {
    this.currentTheme = null
    const src = this.musicSource
    this.musicSource = null
    if (src) {
      try {
        src.stop()
      } catch {
        /* ignore */
      }
    }
  }
}

function clamp01(v: number) {
  return Math.max(0, Math.min(1, v))
}
